import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import type { EventView } from '@grc/shared';
import { t } from '../../i18n';
import {
  addMonths,
  byDay,
  clubDate,
  dayHeading,
  lastDateOf,
  lastMadeDate,
  longDate,
  monthOf,
  monthTitle,
  monthWeeks,
  readDay,
  readMonth,
  WEEK_COLUMNS,
} from './dates';
import { EventDays } from './EventList';
import { pastEvents, upcomingEvents } from './events-api';
import { Unloaded, useLoad, type NotLoaded } from './Load';

/**
 * The past list's pages so far: their events, newest first; whether older ones are left; whether
 * the first has come; and a failure.
 */
type PastPages = { events: EventView[]; more: boolean; asked: boolean; error?: unknown };

/**
 * The month view (B3; D34; the owner's answer, 3A), at /events/month: the month's grid, Monday
 * first (D10), a dot on each day with events (hollow when they're all cancelled), today marked; a
 * tap on a day lists its events under the grid, today's at first. The arrows go as far as there
 * are events. It reads the upcoming list and the past list's pages, asking for the older pages a
 * month needs as the member goes back (no range route, D33). The month and the day are the link's
 * (`?month=2026-10&day=2026-10-20`), so Back from an event's page comes back to them.
 */
export function Month() {
  // The screen's moment: today, and where the weekly events stop.
  const [now] = useState(() => Date.now());
  const today = clubDate(now);
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const month = readMonth(params.get('month')) ?? monthOf(today);
  const chosen = readDay(params.get('day'), month);

  const upcoming = useLoad(upcomingEvents);
  const past = usePastPages(`${month}-01`);

  const coming = upcoming.state.status === 'loaded' ? upcoming.state.value : [];
  const events = new Map<string, EventView[]>();
  for (const day of byDay(unique([...coming, ...past.pages.events]), 'soonest')) {
    events.set(day.date, day.events);
  }

  // What hasn't come yet, or failed: the list under the grid waits for both lists, the past one as
  // far back as the month.
  let waiting: NotLoaded | undefined;
  if (upcoming.state.status === 'failed') waiting = upcoming.state;
  else if (past.pages.error !== undefined) waiting = { status: 'failed', error: past.pages.error };
  else if (upcoming.state.status === 'loading' || !past.covered) waiting = { status: 'loading' };

  function retry() {
    if (upcoming.state.status === 'failed') upcoming.retry();
    if (past.pages.error !== undefined) past.retry();
  }

  // The arrows, once the lists say how far the events go: back while there may be older ones, on
  // to the month of the last one coming.
  const known = waiting === undefined;
  const oldest = past.pages.events[past.pages.events.length - 1] ?? coming[0];
  const newest = coming[coming.length - 1];
  const first =
    oldest === undefined ? monthOf(today) : min(monthOf(today), monthOf(clubDate(oldest.startsAt)));
  const last =
    newest === undefined ? monthOf(today) : max(monthOf(today), monthOf(clubDate(newest.startsAt)));
  const canGoBack = known && (month > first || past.pages.more);
  const canGoOn = known && month < last;

  // The day shown under the grid: the one tapped; today, in this month; or the month's first day
  // with events.
  const firstWithEvents = [...events.keys()].find((date) => monthOf(date) === month);
  const shown = chosen ?? (month === monthOf(today) ? today : firstWithEvents);

  function go(months: number) {
    setParams({ month: addMonths(month, months) }, { replace: true });
  }

  function choose(date: string) {
    setParams({ month, day: date }, { replace: true });
  }

  const from = `${location.pathname}?month=${month}${shown === undefined ? '' : `&day=${shown}`}`;

  return (
    <div className="month">
      <div className="month-head">
        <button
          type="button"
          className="month-arrow"
          aria-label={t('month.previous')}
          onClick={() => go(-1)}
          disabled={!canGoBack}
        />
        <h2>{monthTitle(month)}</h2>
        <button
          type="button"
          className="month-arrow month-arrow-next"
          aria-label={t('month.next')}
          onClick={() => go(1)}
          disabled={!canGoOn}
        />
      </div>
      <table className="month-grid">
        <thead>
          <tr>
            {WEEK_COLUMNS.map(({ short, long }) => (
              <th key={short} scope="col">
                <abbr title={t(long)}>{t(short)}</abbr>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {monthWeeks(month).map((week) => (
            <tr key={week.find((date): date is string => date !== null)}>
              {week.map((date, column) => (
                <td key={date ?? `none-${column}`}>
                  {date !== null && (
                    <Day
                      date={date}
                      today={today}
                      events={events.get(date) ?? []}
                      selected={date === shown}
                      onChoose={choose}
                    />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {lastDateOf(month) > lastMadeDate(today) && (
        <p className="muted small month-ahead">{t('month.ahead')}</p>
      )}
      <div className="month-day-list">
        {waiting !== undefined ? (
          <Unloaded state={waiting} onRetry={retry} />
        ) : shown === undefined ? (
          <p className="muted">{t('month.none')}</p>
        ) : (
          <ShownDay date={shown} today={today} events={events.get(shown) ?? []} from={from} />
        )}
      </div>
    </div>
  );
}

function min(a: string, b: string): string {
  return a < b ? a : b;
}

function max(a: string, b: string): string {
  return a > b ? a : b;
}

/** The events once each: one that ended between the two lists' answers could be in both. */
function unique(events: EventView[]): EventView[] {
  return [...new Map(events.map((event) => [event.id, event])).values()];
}

type DayProps = {
  date: string;
  today: string;
  events: readonly EventView[];
  selected: boolean;
  onChoose: (date: string) => void;
};

/** A day of the grid: its number, its dot, and, for screen readers, its date and its events. */
function Day({ date, today, events, selected, onChoose }: DayProps) {
  const long = longDate(date, today);
  const name =
    events.length === 0
      ? long
      : events.length === 1
        ? t('month.dayEventsOne', { date: long })
        : t('month.dayEvents', { date: long, count: events.length });
  const cancelled = events.length > 0 && events.every((event) => event.status === 'cancelled');
  return (
    <button
      type="button"
      className="month-day"
      aria-label={name}
      aria-pressed={selected}
      aria-current={date === today ? 'date' : undefined}
      onClick={() => onChoose(date)}
    >
      {Number(date.slice(8))}
      {events.length > 0 && (
        <span className={cancelled ? 'month-dot month-dot-cancelled' : 'month-dot'} aria-hidden />
      )}
    </button>
  );
}

/** The day tapped (or today), under the grid: its events in time order, or that it has none. */
function ShownDay({
  date,
  today,
  events,
  from,
}: {
  date: string;
  today: string;
  events: EventView[];
  from: string;
}) {
  if (events.length === 0) {
    return (
      <>
        <h3>{dayHeading(date, today)}</h3>
        <p className="muted">{t('month.noEvents')}</p>
      </>
    );
  }
  return <EventDays days={[{ date, events }]} today={today} from={from} level={3} />;
}

/**
 * The past list's pages, the first at once and then, while there are more, the next ones until the
 * oldest loaded starts before `until` (a date): then every past event from `until` on is here.
 * `covered` says so; `retry` asks again after a failure.
 */
function usePastPages(until: string): { pages: PastPages; covered: boolean; retry: () => void } {
  const [pages, setPages] = useState<PastPages>({ events: [], more: true, asked: false });
  // The page asked for and not answered yet, by the event it comes before ('' for the first).
  const asking = useRef<string | undefined>(undefined);

  const oldest = pages.events[pages.events.length - 1];
  const covered =
    pages.asked && (!pages.more || (oldest !== undefined && clubDate(oldest.startsAt) < until));

  useEffect(() => {
    if (covered || pages.error !== undefined) return;
    const before = pages.events[pages.events.length - 1]?.id;
    const key = before ?? '';
    if (asking.current === key) return;
    asking.current = key;
    pastEvents(before).then(
      (page) =>
        setPages((now) =>
          // Only onto the pages it was asked after.
          (now.events[now.events.length - 1]?.id ?? '') === key
            ? // A page with nothing in it ends the list, whatever it says.
              {
                events: [...now.events, ...page.events],
                more: page.more && page.events.length > 0,
                asked: true,
              }
            : now,
        ),
      (error: unknown) => setPages((now) => ({ ...now, error })),
    );
  }, [covered, pages]);

  function retry() {
    asking.current = undefined;
    setPages(({ error: _error, ...rest }) => rest);
  }

  return { pages, covered, retry };
}
